import { z } from "zod";

import { paginationMetaSchema } from "../interfaces/base-api-response";

/** Mirrors backend `RefundRequestStatus` (`src/refund-request/constant/refund-request.constant.ts`). */
export enum RefundRequestStatus {
  PENDING_APPROVAL = "PENDING_APPROVAL",
  PROCESSING = "PROCESSING",
  INITIATED = "INITIATED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REJECTED = "REJECTED",
}

/** Mirrors backend `RefundRequestTriggerType`. */
export enum RefundRequestTriggerType {
  FULFILLMENT = "FULFILLMENT",
  EDIT = "EDIT",
  CANCELLATION = "CANCELLATION",
  SESSION_CANCELLATION = "SESSION_CANCELLATION",
}

/** Mirrors backend `RefundRequestSource`. */
export enum RefundRequestSource {
  ADMIN = "ADMIN",
  SUPPORT = "SUPPORT",
  SYSTEM = "SYSTEM",
}

/**
 * Statuses where the refund is decided and no more money will move. Everything else is still in
 * flight and is what an admin looking at an order needs to know about.
 */
export const TERMINAL_REFUND_REQUEST_STATUSES: ReadonlySet<RefundRequestStatus> = new Set([
  RefundRequestStatus.COMPLETED,
  RefundRequestStatus.FAILED,
  RefundRequestStatus.REJECTED,
]);

export function isRefundRequestOpen(status: RefundRequestStatus): boolean {
  return !TERMINAL_REFUND_REQUEST_STATUSES.has(status);
}

/**
 * Whether the request still needs an admin decision on the tasks page.
 *
 * Narrower than {@link isRefundRequestOpen}: approval moves the request straight to `PROCESSING`
 * (see backend `REFUND_REQUEST_STATUS_TRANSITIONS`), so `PROCESSING` and `INITIATED` are open but
 * already approved — the gateway is working on them and there is no task left to action.
 */
export function isRefundRequestAwaitingApproval(status: RefundRequestStatus): boolean {
  return status === RefundRequestStatus.PENDING_APPROVAL;
}

/**
 * An entry in `refund_requests.affected_items`. Only `SESSION_CANCELLATION` refunds populate it —
 * `EDIT` and `CANCELLATION` refunds leave it null, because they are raised against the order as a
 * whole and have no single session to point at.
 */
export const refundRequestAffectedItemSchema = z
  .object({
    sessionId: z.string().nullish(),
    subscriptionId: z.string().nullish(),
    changeRequestId: z.string().nullish(),
  })
  .passthrough();

export const refundRequestResponseSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  userId: z.string(),
  triggerType: z.nativeEnum(RefundRequestTriggerType),
  /** Rupees, already converted by the backend — the column itself is paise. */
  amountRupees: z.number(),
  status: z.nativeEnum(RefundRequestStatus),
  reason: z.string(),
  reasonCode: z.string().nullish(),
  source: z.nativeEnum(RefundRequestSource),
  notes: z.string().nullish(),
  affectedItems: z.array(refundRequestAffectedItemSchema).nullish(),
  gatewayRefundId: z.string().nullish(),
  coinsReversed: z.number().nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string(),
  updatedBy: z.string(),
});

export const refundRequestListResponseSchema = z.object({
  items: z.array(refundRequestResponseSchema),
  /** Optional so a bare-array response still parses — callers here only ever need `items`. */
  meta: paginationMetaSchema.optional(),
});

export const listRefundRequestsRequestSchema = z.object({
  orderId: z.string().uuid().optional(),
  paymentId: z.string().uuid().optional(),
  userId: z.string().uuid().optional(),
  status: z.nativeEnum(RefundRequestStatus).optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(50).default(50),
});

export type RefundRequestAffectedItemDto = z.infer<typeof refundRequestAffectedItemSchema>;
export type RefundRequestResponseDto = z.infer<typeof refundRequestResponseSchema>;
export type RefundRequestListResponseDto = z.infer<typeof refundRequestListResponseSchema>;
export type ListRefundRequestsRequestDto = z.input<typeof listRefundRequestsRequestSchema>;
